import { yupResolver } from "@hookform/resolvers/yup";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { useCreateCustomerLocationTransport, useListTransport } from "hooks";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Switch } from "antd";
import * as yup from "yup";
import useCustomHook from "../useCustomHook";

const CreateLocationTransportModal = ({
  customerId,
  isOpenCreateModal,
  toggleCreateModal,
  fetchCustomerLocationTransports,
}) => {
  const { t } = useTranslation(["customer"]);
  const [is_level_2, setIsLevel2] = useState(true);

  const getValidationSchema = () => {
    return yup.object().shape({
      transport_id: yup
        .object()
        .required(
          t("form.validate.select", {
            title: t("customer_location_transport_table.transport"),
          })
        )
        .nullable(),
      province_code: yup
        .object()
        .required(
          t("form.validate.select", {
            title: t("customer_location_transport_table.province"),
          })
        )
        .nullable(),
      district_code: yup
        .object()
        .required(
          t("form.validate.select", {
            title: is_level_2
              ? "Phường xã"
              : t("customer_location_transport_table.district"),
          })
        )
        .nullable(),
      ward_code: yup.object().when([], {
        is: () => is_level_2,
        then: yup.object().nullable(),
        otherwise: yup
          .object()
          .nullable()
          .required(
            t("form.validate.select", {
              title: t("customer_location_transport_table.ward"),
            })
          ),
      }),
      hub_id: yup
        .object()
        .required(
          t("form.validate.select", {
            title: t("customer_location_transport_table.hub"),
          })
        )
        .nullable(),
    });
  };

  const createForm = useForm({
    resolver: yupResolver(getValidationSchema()),
    mode: "all",
    reValidateMode: "onChange",
  });

  const {
    formState: { errors },
    reset,
    getValues,
    setValue,
    control,
    trigger,
    watch,
  } = createForm;

  const hubWatch = watch("hub_id");
  const provinceWatch = watch("province_code");
  const districtWatch = watch("district_code");

  const {
    provinceOptions,
    districtOptions,
    isDisableDistrictSelect,
    wardOptions,
    isDisableWardSelect,
  } = useCustomHook({
    mainForm: createForm,
    is_level_2,
  });

  const onSaveCreate = () => {
    trigger().then(result => {
      if (result) {
        const formValue = getValues();
        const data = {
          customer_id: customerId,
        };
        Object.keys(formValue).forEach(key => {
          if (key === "ward_code" && is_level_2) {
            data[key] = null;
          } else {
            data[key] = formValue[key]?.value;
          }
        });
        createCustomerLocationTransport(data);
      }
    });
  };

  const onSuccesCreate = () => {
    toggleCreateModal();
    fetchCustomerLocationTransports();
    reset({});
    setIsLevel2(true);
  };

  const { mutate: createCustomerLocationTransport } =
    useCreateCustomerLocationTransport(onSuccesCreate);

  const handleChangeIsLevel2 = newIsLevel2 => {
    setIsLevel2(newIsLevel2);
    setValue("province_code", null);
    setValue("district_code", null);
    setValue("ward_code", null);
    setValue("hub_id", null);
    setValue("transport_id", null);
  };

  const cancelCreate = () => {
    reset({});
    setIsLevel2(true);
    toggleCreateModal("close");
  };

  const [transportParams, setTransportParams] = useState({
    sizePerPage: 100,
    columnFilter: {
      "filter[record_status]": "O",
    },
    fields: "id,name",
  });

  const { data: transportList, refetch: fetchTransportList } = useListTransport(
    {
      params: transportParams,
    }
  );

  const optionsTransportSelect = useMemo(() => {
    return transportList?.items?.map(item => ({
      label: item.name,
      value: item.id,
    }));
  }, [transportList]);

  useEffect(() => {
    fetchTransportList();
  }, [transportParams]);

  useEffect(() => {
    if (hubWatch) {
      setValue("transport_id", null);
      setTransportParams(prev => ({
        ...prev,
        columnFilter: {
          ...prev.columnFilter,
          "filter[hub_id]": hubWatch.value,
        },
      }));
    }
  }, [hubWatch]);

  return (
    <Modal
      size="lg"
      isOpen={isOpenCreateModal}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={cancelCreate}
    >
      <ModalHeader toggle={cancelCreate}>
        {t("customer_location_transport_table.title.create", {
          target: t("customer_location_transport_table.transport"),
        })}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col xs={12}>
            <FormGroup>
              <Label
                className="d-flex align-items-center gap-1"
                style={{ cursor: "pointer" }}
                htmlFor="is_level_2"
                onClick={() => handleChangeIsLevel2(!is_level_2)}
              >
                <Switch checked={is_level_2} onChange={handleChangeIsLevel2} />
                Địa chỉ 2 cấp
              </Label>
            </FormGroup>
          </Col>

          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="province_code">
                {t("customer_location_transport_table.province")}{" "}
                <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="province_code"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("customer_location_transport_table.province"),
                    })}
                    options={provinceOptions}
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.province_code && (
                <MessageFormValidate error={errors.province_code.message} />
              )}
            </FormGroup>
          </Col>

          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="district_code">
                {is_level_2
                  ? "Phường xã"
                  : t("customer_location_transport_table.district")}{" "}
                <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="district_code"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: is_level_2
                        ? "Phường xã"
                        : t("customer_location_transport_table.district"),
                    })}
                    options={districtOptions}
                    isDisabled={isDisableDistrictSelect}
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.district_code && (
                <MessageFormValidate error={errors.district_code.message} />
              )}
            </FormGroup>
          </Col>

          {!is_level_2 && (
            <Col xs={6}>
              <FormGroup>
                <Label htmlFor="ward_code">
                  {t("customer_location_transport_table.ward")}{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Controller
                  control={control}
                  name="ward_code"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t("placeholder.select", {
                        title: t("customer_location_transport_table.ward"),
                      })}
                      options={wardOptions}
                      isDisabled={isDisableWardSelect}
                      isSearchable
                      className="basic-single w-100"
                      classNamePrefix="select"
                    />
                  )}
                />
                {errors.ward_code && (
                  <MessageFormValidate error={errors.ward_code.message} />
                )}
              </FormGroup>
            </Col>
          )}

          <Col xs={6}>
            <FormHubSelect
              isMyHubOnly
              isRequired
              mainForm={createForm}
              fieldName="hub_id"
              t={t}
              title={t("customer_location_transport_table.hub")}
              addressLevel={is_level_2 ? 2 : 3}
              provinceCode={provinceWatch?.value}
              districtCode={districtWatch?.value}
              key={`hub-select-${is_level_2}`}
            />
          </Col>

          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="transport_id">
                {t("customer_location_transport_table.transport")}{" "}
                <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="transport_id"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("customer_location_transport_table.transport"),
                    })}
                    options={optionsTransportSelect || []}
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isDisabled={!hubWatch}
                  />
                )}
              />
              {errors.transport_id && (
                <MessageFormValidate error={errors.transport_id.message} />
              )}
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          size="xs"
          onClick={cancelCreate}
          className="btn btn-danger btn-label"
        >
          <i className="fas fa-ban label-icon"></i>
          {t("customer_location_transport_table.actions.cancel")}
        </Button>
        <Button
          size="xs"
          style={{ backgroundColor: "#3C64B1" }}
          onClick={onSaveCreate}
          className="btn btn-primary btn-label"
        >
          <i className="fas fa-save label-icon"></i>
          {t("customer_location_transport_table.actions.save")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateLocationTransportModal;
